import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Box } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type { ColDef } from "ag-grid-community";
import { apiPut } from "../../services/genesisApi/httpsMethods";
import { useReferenceLists } from "../../context/GlobalGetLists";
import { CustomButton } from "../../components/common/customButton";
import { areRecordsEqual, getChangedRecords } from "../../utils/compareData";
import { CustomTextInput } from "../../components/common/inputFields/customTextInput";
import { SimpleToolbar } from "../../components/layout/Toolbar";
import { CustomIconButton } from "../../components/common/customIconButton";
import type { Contact } from "../../types/deal";
import { HyperLinkRenderer } from "../../utils/hyperlinkRenderer";
import pencilIcon from "@/assets/svg/edit.svg";
import downloadIcon from "@/assets/svg/downloads-dark.svg";
import "../../assets/css/Text-General.css";
import { useCurrentUser } from "@/context/CurrentUserContext";
import "../../assets/css/AGGrid-custom.css";
import { useGridEditCommit } from "@/hooks/useGridEditCommit";

const Contacts = () => {
  const { canEdit } = useCurrentUser();
  const [editMode, setEditMode] = useState(false);
  const gridRef = useRef<AgGridReact>(null);
  const [filter, setFilter] = useState("");
  const [dirtyGrid, setDirtyGrid] = useState(false);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const [newContacts, setNewContacts] = useState<Contact[]>([]);
  const [initialContacts, setInitialContacts] = useState<Contact[]>([]);

  const { contacts, sponsors, deals, sources } = useReferenceLists();

  // Add derived values on load
  useEffect(() => {
    if (!contacts) return;
    // Clone so grid edits never mutate the shared reference list.
    setNewContacts(structuredClone(contacts));
    setInitialContacts(structuredClone(contacts));
  }, [contacts]);

  // Refresh grid when edit mode changes to ensure cells become editable
  useEffect(() => {
    if (gridRef.current && editMode) {
      // Small delay to ensure column definitions have updated
      setTimeout(() => {
        gridRef.current?.api.refreshCells({ force: true });
      }, 0);
    }
  }, [editMode]);

  // Track if grid is dirty
  useEffect(() => {
    const isEqual = areRecordsEqual(initialContacts, newContacts);
    setDirtyGrid(!isEqual);
  }, [initialContacts, newContacts]);

  // Track validation errors from the current row data
  useEffect(() => {
    const hasErrors = newContacts.some(
      (contact: any) =>
        contact?.emailError === true || contact?.phoneError === true,
    );
    setHasValidationErrors(hasErrors);
  }, [newContacts]);

  const onCellValueChanged = useCallback((event: any) => {
    const { newValue, oldValue, data } = event;
    if (newValue === oldValue) return;

    setNewContacts((prevContact: Contact[]) =>
      prevContact.map((contact) =>
        contact.id === data.id ? { ...contact, ...data } : contact,
      ),
    );
  }, []);

  const handleCancelButtonPress = () => {
    setEditMode(false);
    // Clone so AG Grid edits don't mutate the baseline rows.
    setNewContacts(structuredClone(initialContacts)); // Reset to original
    setDirtyGrid(false);
    setHasValidationErrors(false);
  };

  const handleSaveButtonPress = async () => {
    if (hasValidationErrors) {
      alert("Please fix validation errors before saving.");
      return;
    }

    try {
      const changedItems = getChangedRecords(newContacts, initialContacts);

      if (changedItems.length === 0) {
        setEditMode(false);
        return;
      }

      const updates = changedItems.map((contact) => {
        if (!contact.id) {
          console.error("Contact missing id:", contact);
          return Promise.resolve({ success: false });
        }
        return apiPut("contact", contact.id, contact).catch((error) => {
          console.error(`Failed to update contact ${contact.id}:`, error);
          return { success: false, error };
        });
      });

      const results = await Promise.all(updates);
      const failed = results.filter((r) => !r?.success);

      if (failed.length > 0) {
        console.error("Some contact updates failed:", failed);
        alert(
          `Failed to update ${failed.length} contact(s). Check console for details.`,
        );
      } else {
        setEditMode(false);
        window.location.reload();
      }
    } catch (error) {
      console.error("Error saving contacts:", error);
      alert("Error saving contacts. Check console for details.");
    }
  };

  const { isCellEditing, requestSave, gridEditProps } =
    useGridEditCommit(handleSaveButtonPress);

  const filteredRows = useMemo(() => {
    return newContacts.filter((s) =>
      s.contact_name?.toLowerCase().includes(filter.toLowerCase()),
    );
  }, [newContacts, filter]);

  const formatLinkNames = useCallback(
    (
      links: any[] | undefined,
      idField: "sponsor_id" | "deal_id" | "source_id" | "entity_id",
      lookup: any[] | undefined,
      nameField: string,
    ) => {
      if (!links?.length) return "-";

      const ids = links
        .map((l) => l?.[idField])
        .filter((v): v is number => typeof v === "number" && v > 0);

      const uniqueIds = Array.from(new Set(ids));
      if (!uniqueIds.length) return "-";

      return uniqueIds
        .map((id) => {
          const match = lookup?.find((x) => x?.id === id);
          const name = match?.[nameField];
          return typeof name === "string" && name.trim() ? name : `ID ${id}`;
        })
        .join(", ");
    },
    [],
  );

  const formatSponsorNamesFromLinks = useCallback(
    (links: any[] | undefined) => {
      if (!links?.length) return "-";

      const directSponsorIds = links
        .map((l) => l?.sponsor_id)
        .filter((v): v is number => typeof v === "number" && v > 0);

      const sponsorIds = Array.from(new Set([...directSponsorIds]));
      if (!sponsorIds.length) return "-";

      return sponsorIds
        .map((id) => {
          const sponsor = sponsors?.find((s: any) => s?.id === id);
          const name = sponsor?.sponsor_name;
          return typeof name === "string" && name.trim() ? name : `ID ${id}`;
        })
        .join(", ");
    },
    [sponsors],
  );

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        field: "contact_name",
        headerName: "Contact",
        editable: editMode,
        width: 150,
        cellRenderer: editMode ? undefined : HyperLinkRenderer("Contacts"),
      },
      {
        field: "company",
        headerName: "Company",
        editable: editMode,
        width: 150,
      },
      {
        field: "contact_email",
        headerName: "Email",
        editable: editMode,
        width: 200,
        valueSetter: (params) => {
          const raw = params.newValue?.trim() || "";

          // Basic email validation
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          const isValid = emailRegex.test(raw);

          // Always save the value
          params.data.contact_email = raw;

          // Track error on row
          params.data.emailError = !isValid;
          if (!isValid) {
            setHasValidationErrors(true);
          }

          return true; // always accept so value shows in grid
        },

        cellClassRules: {
          "cell-error": (params) => params.data.emailError === true,
        },
      },
      {
        field: "contact_title",
        headerName: "Job Title",
        editable: editMode,
        width: 125,
      },
      {
        field: "contact_phone",
        headerName: "Phone",
        editable: editMode,
        width: 125,
        valueSetter: (params) => {
          const raw = params.newValue?.trim();

          const basicFormat = /^[\d+\s()-]{7,20}$/;
          const digitsOnly = raw.replace(/\D/g, "");

          const isValid =
            basicFormat.test(raw) &&
            digitsOnly.length >= 7 &&
            digitsOnly.length <= 15;

          params.data.contact_phone = raw;

          params.data.phoneError = !isValid;
          if (!isValid) {
            setHasValidationErrors(true);
          }

          return true; // always accept so value shows in grid
        },

        cellClassRules: {
          "cell-error": (params) => params.data.phoneError === true,
        },
      },
      {
        headerName: "Sponsors",
        colId: "link_sponsors",
        editable: false,
        width: 150,
        valueGetter: (params: any) =>
          formatSponsorNamesFromLinks(params?.data?.links),
      },
      {
        headerName: "Deals",
        colId: "link_deals",
        editable: false,
        width: 220,
        valueGetter: (params: any) =>
          formatLinkNames(params?.data?.links, "deal_id", deals, "deal_name"),
      },
      {
        headerName: "Sources",
        colId: "link_sources",
        editable: false,
        width: 220,
        valueGetter: (params: any) =>
          formatLinkNames(
            params?.data?.links,
            "source_id",
            sources,
            "source_name",
          ),
      },
      {
        field: "contact_location",
        headerName: "Location",
        editable: editMode,
        width: 200,
      },
    ],
    [
      editMode,
      sponsors,
      deals,
      sources,
      formatLinkNames,
      formatSponsorNamesFromLinks,
    ],
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        padding: 0,
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <SimpleToolbar>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "15px",
              minWidth: 0,
            }}
          >
            <CustomTextInput
              label="Contact name"
              value={filter}
              onChange={(val) => {
                if (typeof val === "string") setFilter(val);
              }}
            />
          </Box>
          {editMode ? (
            <Box
              sx={{ display: "flex", alignItems: "center", gap: "15px" }}
              data-position="right"
            >
              <CustomButton
                text="Cancel"
                handleButtonPress={handleCancelButtonPress}
                isButtonDisabled={false}
                solid={false}
                widthOverride="100px"
              />
              <CustomButton
                text="Save"
                handleButtonPress={requestSave}
                isButtonDisabled={(!dirtyGrid && !isCellEditing) || hasValidationErrors}
                solid
                widthOverride="100px"
              />
            </Box>
          ) : (
            <Box
              data-position="right"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                ml: -0.5,
                "& .MuiIconButton-root": {
                  p: 0.6,
                },
                "& .MuiIconButton-root .MuiSvgIcon-root": {
                  fontSize: 22,
                },
              }}
            >
              <CustomIconButton
                tooltip="Download"
                icon={
                  <Box
                    component="img"
                    src={downloadIcon}
                    alt="Download contacts"
                    sx={{ width: 30, height: 30, display: "block" }}
                  />
                }
                iconButtonSx={{ p: 0 }}
                onClick={() =>
                  gridRef.current?.api.exportDataAsCsv({ fileName: "contacts" })
                }
              />
              <CustomIconButton
                tooltip="Edit"
                icon={
                  <Box
                    component="img"
                    src={pencilIcon}
                    alt="Edit contacts"
                    sx={{ width: 20, height: 20, display: "block" }}
                  />
                }
                disabled={!canEdit}
                onClick={() => {
                  if (!canEdit) return;
                  setEditMode(true);
                }}
              />
            </Box>
          )}
        </SimpleToolbar>
      </Box>

      {/* AG Grid */}
      <Box sx={{ flexGrow: 1, minHeight: 0, p: 0 }}>
        <div
          className="ag-theme-alpine"
          style={{ width: "100%", height: "100%" }}
        >
          <AgGridReact
            {...gridEditProps}
            rowData={filteredRows}
            ref={gridRef}
            columnDefs={columnDefs}
            onCellValueChanged={onCellValueChanged}
            singleClickEdit={true}
            defaultColDef={{ resizable: true }}
            rowHeight={30}
            getRowId={({ data }) => data?.id?.toString()}
          />
        </div>
      </Box>
    </Box>
  );
};

export default Contacts;
